import { CommandBar, DetailsList, Panel, PanelType, Spinner, Selection, SelectionMode, MessageBar, MessageBarType, ActionButton, IColumn } from "office-ui-fabric-react"
import { useEffect, useState } from "react";
import { Route, RouteComponentProps, useHistory } from "react-router-dom";
import { deleteSite, getSites } from "../../../api/siteslocations";
import { LocationsPanel } from "./LocationsPanel";
import { SiteFormPanel } from "../forms/SiteFormPanel";
import { ISite } from "../../../types/SiteLocation.types"

export interface ISitesPanelProps {
    onDismiss: () => void;
}

export const SitesPanel = (props: ISitesPanelProps) => {
    const history = useHistory();

    const [ sites, setSites ] = useState<Partial<ISite>[]>();
    const [ error, setError ] = useState<string>();
    const [ loading, setLoading ] = useState(false);
    const [ siteSelected, setSiteSelected ] = useState(false);

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as ISite[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setSiteSelected(true);
        } else {
            setSiteSelected(false);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const _loadSites = async () => {
        setLoading(true);
        selection.setAllSelected(false);
        setSiteSelected(false);
        const sitesResponse = await getSites();
        if (sitesResponse.ok) {
            setSites(await sitesResponse.json());
        } else {
            setError("Unable to load sites: " + sitesResponse.statusText);
        }
        setLoading(false);
    };

    const _deleteSite = async (id: number) => {
        const deleteResponse = await deleteSite(id);
        if (deleteResponse.ok) {
            _loadSites();
        } else {
            setError("Unable to delete site: " + deleteResponse.statusText)
        }
    }

    useEffect(() => {
        _loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel 
            headerText="Sites" 
            isOpen={true} 
            onDismiss={props.onDismiss}
            type={PanelType.medium} 
        >
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            <p><strong>Create or edit new sites</strong></p>
            <p>
                Sites are geographically disparate locations like Hospitals, Offices and other places that host desks 
                or meeting rooms. Using this menu, you can create sites, on-site locations (such as buildings) and 
                within those - floorplan layouts for desk booking.
            </p>
            <CommandBar
                items={[
                    {
                        key: "new",
                        text: "New",
                        iconProps: { iconName: "Add" },
                        onClick: () => history.push("/menu/settings/sites/new")
                    },
                    {
                        disabled: !siteSelected,
                        key: "edit",
                        text: "Edit",
                        iconProps: { iconName: "Edit" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as ISite[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                history.push(`/menu/settings/sites/${selectedItem.Id}/edit`);
                            }
                        }
                    },
                    {
                        disabled: !siteSelected,
                        key: "delete",
                        text: "Delete",
                        iconProps: { iconName: "Delete" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as ISite[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                if (window.confirm("Are you sure? Note: Please make sure there are no bookings active for this site before deleting.")) {
                                    _deleteSite(selectedItem.Id);
                                }
                            }
                        }
                    }
                ]}
            />
            { loading === true && (
                <Spinner />
            )}
            { loading === false && sites !== undefined && sites.length > 0 && (
                <DetailsList
                    items={sites}
                    columns={[
                        {
                            key: "name",
                            fieldName: "Title",
                            name: "Name",
                            minWidth: 40
                        },
                        {
                            key: "locations",
                            fieldName: "Locations",
                            name: "ID",
                            minWidth: 120,
                            onRender: (item: ISite, index?: number, column?: IColumn) => {
                                return <ActionButton styles={{flexContainer: { alignItems: "flex-start" }}} iconProps={{iconName: "CityNext"}} text="Locations" onClick={() => {
                                    history.push(`/menu/settings/sites/${item.Id}/locations`);
                                }} />
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            )}
            <Route path="/menu/settings/sites/:id/locations" component={(route: RouteComponentProps<{id?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.id))) {
                    return <LocationsPanel siteId={Number(route.match.params.id)} onDismiss={() => history.goBack()} />;
                }
                return null;
            }} />
            <Route path="/menu/settings/sites/new" exact={true}>
                <SiteFormPanel site={{}} onDismiss={(reloadSites) => {
                    history.goBack();
                    if (reloadSites) {
                        _loadSites();
                    }
                }} />
            </Route>
            <Route path="/menu/settings/sites/:id/edit" exact={true} component={(route: RouteComponentProps<{id?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.id))) {
                    const matchingSite = sites?.filter((s) => s.Id === Number(route.match.params.id))[0];
                    if (matchingSite) {
                        return <SiteFormPanel site={matchingSite} onDismiss={(reloadSites) => {
                            history.goBack();
                            if (reloadSites) {
                                _loadSites();
                            }
                        }} />
                    }
                }
                return null;
            }} />
        </Panel>
    )
}